/**
 * Module-level session state for the instant-add task flow.
 *
 * The instant-add flow (createInstantTask in useProjectTasksTable) inserts an
 * optimistic row with a tempId and lets the user edit it immediately, before
 * the server has assigned a real id. We need to:
 *
 *   1. Remember the realId once the create call resolves, so any field updates
 *      that arrive after that point can be sent to the server against the real
 *      task.
 *   2. Buffer any field updates the user makes while the create is still in
 *      flight, and drain them once the realId is known.
 *
 * Keeping these in module-level Maps (rather than React state) avoids tearing
 * across renders and lets us read/write from inside mutation callbacks.
 */

export interface PendingFieldUpdate {
  field: string
  value: unknown
}

export const tempToRealId = new Map<string, string>()
const realToTempId = new Map<string, string>()
export const pendingFieldUpdates = new Map<string, PendingFieldUpdate[]>()

/**
 * Set of tempIds for in-flight instant-add creates, indexed by moduleId. Read
 * by useTasksRealtime so it can skip its onTaskCreated handler when an
 * instant-add session for the same module is still in flight — otherwise the
 * realtime event swaps tempId → realId in the cache, which remounts the row
 * and kicks the user out of edit mode.
 */
const pendingInstantAddByModuleId = new Map<string, Set<string>>()

export function markInstantAddPending(moduleId: string, tempId: string): void {
  let set = pendingInstantAddByModuleId.get(moduleId)
  if (!set) {
    set = new Set()
    pendingInstantAddByModuleId.set(moduleId, set)
  }
  set.add(tempId)
}

export function clearInstantAddPending(moduleId: string, tempId: string): void {
  const set = pendingInstantAddByModuleId.get(moduleId)
  if (!set) return
  set.delete(tempId)
  if (set.size === 0) pendingInstantAddByModuleId.delete(moduleId)
}

export function hasInstantAddPending(moduleId: string): boolean {
  const set = pendingInstantAddByModuleId.get(moduleId)
  return !!set && set.size > 0
}

/**
 * Per-tempId cache-subscription unsubscribe handles. createInstantTask attaches
 * a subscriber that re-injects our optimistic row whenever a refetch overwrites
 * the cache. We unsubscribe when the session is complete.
 */
const sessionUnsubscribers = new Map<string, () => void>()

export function setSessionUnsubscriber(
  tempId: string,
  unsub: () => void,
): void {
  sessionUnsubscribers.get(tempId)?.()
  sessionUnsubscribers.set(tempId, unsub)
}

export function endSession(tempId: string): void {
  sessionUnsubscribers.get(tempId)?.()
  sessionUnsubscribers.delete(tempId)
}

export function rememberRealId(tempId: string, realId: string): void {
  tempToRealId.set(tempId, realId)
  realToTempId.set(realId, tempId)
}

export function resolveRealId(tempId: string): string | undefined {
  return tempToRealId.get(tempId)
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Server ids for a set of table rows, keyed by row id. Instant-add rows stay
 * keyed by their tempId in the caches, so bulk writes must send the realId
 * instead; a row whose create is still in flight has no server id yet and is
 * left out (bulk endpoints reject the whole request for one non-UUID id).
 */
export function persistedTaskIds(
  rowIds: readonly string[],
): Map<string, string> {
  const ids = new Map<string, string>()
  for (const rowId of rowIds) {
    const serverId = rowId.startsWith('temp-') ? resolveRealId(rowId) : rowId
    if (serverId && UUID_PATTERN.test(serverId)) ids.set(rowId, serverId)
  }
  return ids
}

/**
 * True while an instant-add edit session is defending the row this task id
 * belongs to (either side of the temp↔real mapping). Realtime task handlers
 * use this to skip echoes of our own in-session updates: those events carry
 * the realId, which is never present in the list caches (the row is keyed by
 * its tempId), so processing them triggers "unknown task" membership
 * invalidations that refetch every active list and yank open editors shut.
 */
export function isInstantAddSessionTaskId(taskId: string): boolean {
  const tempId = taskId.startsWith('temp-') ? taskId : realToTempId.get(taskId)
  return !!tempId && sessionUnsubscribers.has(tempId)
}

export function bufferFieldUpdate(
  tempId: string,
  field: string,
  value: unknown,
): void {
  const existing = pendingFieldUpdates.get(tempId) ?? []
  // If the same field is buffered multiple times, keep the latest value only.
  const filtered = existing.filter(u => u.field !== field)
  filtered.push({ field, value })
  pendingFieldUpdates.set(tempId, filtered)
}

export function drainFieldUpdates(tempId: string): PendingFieldUpdate[] {
  const updates = pendingFieldUpdates.get(tempId) ?? []
  pendingFieldUpdates.delete(tempId)
  return updates
}

export function forgetTempId(tempId: string): void {
  tempToRealId.delete(tempId)
  pendingFieldUpdates.delete(tempId)
}

/**
 * Resolve the API-facing id for a delete of a possibly temp-keyed task, and
 * tear the instant-add session down in the same step: the row is going away,
 * so the cache defender must stop resurrecting it and the temp→real mapping
 * can be dropped. Returns null for a temp id whose create hasn't resolved yet
 * (nothing exists server-side to delete — practically unreachable, since
 * confirming a delete takes longer than the create round-trip).
 */
export function resolveInstantAddDeleteTarget(taskId: string): string | null {
  if (!taskId.startsWith('temp-')) return taskId
  const realId = tempToRealId.get(taskId) ?? null
  endSession(taskId)
  forgetTempId(taskId)
  return realId
}
